/**
 * Post-deploy review item #10 — orders list "送货地址" column was
 * rendering Shopify shipping_address JSON via the default
 * `JsonKeyValueTable` (one row per key dumped on screen). This
 * helper compresses a Shopify shipping_address object — single
 * address, NOT the addresses[] array used on customers — into one
 * readable line:
 *
 *     "address1, address2, city, province, zip, country"
 *
 * Missing / null / empty fields are dropped cleanly so the rendered
 * string never contains double commas, leading commas, or the literal
 * "null" / "undefined". Null / undefined / empty inputs collapse to
 * "—" so the column never reads blank against the table grid.
 *
 * Input shape: either a Shopify shipping_address object (live
 * webhook payload) OR the JSON-encoded string variant that
 * `services/orders.ts` declares (`shipping_address?: string`). Both
 * are accepted. Malformed JSON falls back to "—" — this is a
 * display-only helper so silent fail is correct here (the parse
 * boundary already lives in the sync writer, EQD §2); duplicating
 * fail-loud here would surface every legacy malformed row across
 * the table and block operators from doing their job.
 */

type Maybe<T> = T | null | undefined;

interface ShopifyShippingAddress {
  address1?: Maybe<string>;
  address2?: Maybe<string>;
  // Domain-shape variant (#1222): the backend `orders.shipping_address`
  // column stores the mapper's domain Address ({line1, line2, …} — see
  // backend mapper.mapShippingAddress / #988), and the list projection
  // returns that object verbatim. Render its street fields too.
  line1?: Maybe<string>;
  line2?: Maybe<string>;
  city?: Maybe<string>;
  province?: Maybe<string | number>;
  zip?: Maybe<string | number>;
  country?: Maybe<string>;
}

const ADDRESS_FIELD_ORDER: ReadonlyArray<keyof ShopifyShippingAddress> = [
  'address1',
  'line1',
  'address2',
  'line2',
  'city',
  'province',
  'zip',
  'country',
] as const;

const EMPTY_DISPLAY = '—'; // em-dash

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

const coerceToObject = (raw: unknown): ShopifyShippingAddress | null => {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return isPlainObject(parsed) ? (parsed as ShopifyShippingAddress) : null;
    } catch {
      return null;
    }
  }
  return isPlainObject(raw) ? (raw as ShopifyShippingAddress) : null;
};

/**
 * The one-line address, or null when the input carries no field at all
 * (#2689). Callers that link or copy the address need "nothing to show" as a
 * value distinct from the "—" placeholder text.
 */
export const formatAddressOrNull = (raw: unknown): string | null => {
  const addr = coerceToObject(raw);
  if (!addr) return null;

  const parts: string[] = [];
  for (const key of ADDRESS_FIELD_ORDER) {
    const v = addr[key];
    if (v == null) continue;
    const s = String(v).trim();
    if (!s) continue;
    parts.push(s);
  }

  return parts.length === 0 ? null : parts.join(', ');
};

export const formatAddress = (raw: unknown): string => formatAddressOrNull(raw) ?? EMPTY_DISPLAY;

// ─── #1148: order-detail address fallback ───────────────────────────────────

// The street/locality fields that make an address "real". `country` is
// deliberately excluded — the domain mapper defaults it to "USA", so a
// null-shipping-address order's legacy skeleton
// `{"line1":"","city":"",…,"country":"USA"}` carries only that default and is
// still semantically empty.
const MEANINGFUL_ADDRESS_KEYS = [
  'line1',
  'address1',
  'line2',
  'address2',
  'city',
  'province',
  'zip',
] as const;

/**
 * True when an order's stored `shipping_address` carries no real address:
 * null / undefined, an empty JSON string, OR the all-empty-string skeleton
 * (both the domain `line1` shape and the Shopify `address1` shape). Accepts an
 * object or its JSON-string form. Used to decide whether the order detail must
 * fall back to the customer's default address.
 */
export const isEmptyOrderAddress = (raw: unknown): boolean => {
  const addr = coerceToObject(raw);
  if (!addr) return true;
  for (const key of MEANINGFUL_ADDRESS_KEYS) {
    const v = addr[key as keyof ShopifyShippingAddress];
    if (v == null) continue;
    if (String(v).trim().length > 0) return false;
  }
  return true;
};

/**
 * Pick the address to display from a customer's Shopify `addresses` array
 * (JSON string): the entry flagged `default: true`, else the first entry.
 * Returns null for null / empty / malformed / empty-array input.
 */
export const pickDefaultCustomerAddress = (
  addressesJson: string | null | undefined,
): Record<string, unknown> | null => {
  if (addressesJson == null) return null;
  const trimmed = addressesJson.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const entries = parsed.filter(isPlainObject);
  if (entries.length === 0) return null;
  const flagged = entries.find((e) => e.default === true);
  return flagged ?? entries[0] ?? null;
};
